<script setup lang="ts">
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import SingleImageUploader from '@/components/SingleImageUploader.vue';
  import { ICurrency } from '@/interfaces/ICurrency';
  import { IOptionItem } from '@/interfaces/IOptionItem';
  import { Globals } from '@/modules/Globals';
  import { computed, ref } from 'vue';

  const props = defineProps<{
    /** Owned by NewItemModal (inside nItem.priceVariants); mutated in place. */
    variant: IOptionItem;
    isDefault: boolean;
    /** True when this is the only active variant left (toggle gets locked). */
    lastActive: boolean;
    currency: ICurrency;
  }>();

  const emit = defineEmits<{
    (e: 'costChanged'): void;
    (e: 'discountedCostChanged'): void;
    (e: 'remove'): void;
  }>();

  const uploadsPath = Globals.uploadsPath;

  const expanded = ref(false);

  const thumbnailUrl = computed(() => {
    if (!props.variant.picture) return null;
    return `${uploadsPath}/${props.variant.picture}`;
  });

  function enableDiscount() {
    props.variant.discountedCost = Math.round(props.variant.cost * 0.9);
    emit('discountedCostChanged');
  }

  function onDiscountBlur() {
    // Clearing a number input leaves '' behind instead of a number
    const raw: number | string | null | undefined =
      props.variant.discountedCost;
    if (raw === '') {
      props.variant.discountedCost = null;
      emit('discountedCostChanged');
    }
  }

  function toggleActive() {
    if (props.lastActive && props.variant.active) return;
    props.variant.active = !props.variant.active;
  }

  /** The whole row toggles expansion, except clicks on its inputs/buttons. */
  function onRowClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.closest('input, textarea, button, label')) return;
    expanded.value = !expanded.value;
  }
</script>

<template>
  <div class="item-variant-row" :class="{ expanded }">
    <div class="variant-row" @click="onRowClick">
      <div class="variant-thumb">
        <img
          v-if="thumbnailUrl"
          class="variant-thumb-img"
          :src="thumbnailUrl"
          alt=""
        />
        <PosUiIcon
          v-else
          name="image-outline"
          class="variant-thumb-placeholder"
        />
      </div>
      <span class="variant-active-dot" :class="{ inactive: !variant.active }" />
      <div class="variant-name">
        <input
          v-model="variant.title"
          class="variant-name-input"
          type="text"
          maxlength="255"
          :placeholder="
            isDefault
              ? $t('itemVariants.defaultVariantName')
              : $t('itemVariants.variantName')
          "
        />
        <span v-if="isDefault" class="variant-default-badge">
          {{ $t('itemVariants.defaultBadge') }}
        </span>
      </div>
      <div class="variant-price">
        <span class="variant-price-symbol">{{ currency.symbol }}</span>
        <input
          v-model="variant.cost"
          class="variant-price-input"
          type="number"
          :placeholder="$t('itemVariants.price')"
          @change="emit('costChanged')"
        />
      </div>
      <div class="variant-discount">
        <label v-if="variant.discountedCost === null" class="discount-toggle">
          <span class="discount-toggle-label">
            {{ $t('itemVariants.discount') }}
          </span>
          <input
            class="discount-toggle-checkbox"
            type="checkbox"
            @change="enableDiscount"
          />
        </label>
        <div v-else class="variant-price">
          <span class="variant-price-symbol">{{ currency.symbol }}</span>
          <input
            v-model="variant.discountedCost"
            class="variant-price-input discounted"
            type="number"
            :placeholder="$t('itemVariants.discountedPrice')"
            @change="emit('discountedCostChanged')"
            @blur="onDiscountBlur"
            @keypress.enter="onDiscountBlur"
          />
        </div>
      </div>
      <button class="variant-expand-button" @click="expanded = !expanded">
        <PosUiIcon :name="expanded ? 'chevron-up' : 'chevron-down'" />
      </button>
    </div>

    <div v-show="expanded" class="variant-details">
      <div class="variant-details-grid">
        <div class="variant-picture">
          <span class="detail-label">{{ $t('itemVariants.picture') }}</span>
          <SingleImageUploader
            v-model="variant.picture"
            :maxWidth="800"
            :maxHeight="800"
          />
        </div>
        <div class="variant-detail-fields">
          <label class="field">
            <span class="detail-label">
              {{ $t('itemVariants.description') }}
            </span>
            <textarea
              v-model="variant.description"
              class="field-textarea"
              rows="2"
              :placeholder="$t('optionEditor.descriptionPlaceholder')"
            />
          </label>
          <div class="variant-detail-actions">
            <div class="active-toggle">
              <v-switch
                :input-value="variant.active"
                :disabled="lastActive && variant.active"
                :label="$t('itemVariants.active')"
                class="active-switch"
                color="green"
                dense
                hide-details
                @change="toggleActive"
              />
              <span v-if="lastActive && variant.active" class="detail-help">
                {{ $t('itemVariants.lastActiveHint') }}
              </span>
            </div>
            <PosUiButton
              color="error"
              variant="soft"
              size="sm"
              icon="delete-outline"
              @click="emit('remove')"
            >
              {{ $t('itemVariants.remove') }}
            </PosUiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .item-variant-row {
    @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-transition-shadow;

    &.expanded {
      @apply tw-shadow-sm;
    }

    .variant-row {
      @apply tw-flex tw-cursor-pointer tw-flex-wrap tw-items-center tw-gap-2 tw-p-2;

      .variant-thumb {
        @apply tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-bg-neutral-100;

        .variant-thumb-img {
          @apply tw-h-full tw-w-full tw-object-cover;
        }

        .variant-thumb-placeholder {
          @apply tw-text-xl tw-text-neutral-400;
        }
      }

      .variant-active-dot {
        @apply tw-h-2 tw-w-2 tw-flex-none tw-rounded-full tw-bg-emerald-500;

        &.inactive {
          @apply tw-bg-neutral-300;
        }
      }

      .variant-name {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5;

        .variant-name-input {
          @apply tw-h-11 tw-w-full tw-cursor-text tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

          &:focus {
            @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
          }
        }

        .variant-default-badge {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }

      .variant-price {
        @apply tw-relative tw-flex tw-flex-none tw-items-center;

        .variant-price-symbol {
          @apply tw-pointer-events-none tw-absolute tw-left-2.5 tw-text-sm tw-text-neutral-400;
        }

        .variant-price-input {
          @apply tw-h-11 tw-w-28 tw-cursor-text tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-pl-6 tw-pr-2 tw-text-sm tw-tabular-nums tw-text-neutral-900 tw-outline-none tw-transition-colors;

          &:focus {
            @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
          }
        }
      }

      .variant-discount {
        @apply tw-flex tw-flex-none tw-items-center;

        .discount-toggle {
          @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-0.5 tw-px-1;

          .discount-toggle-label {
            @apply tw-text-xs tw-text-neutral-500;
          }

          .discount-toggle-checkbox {
            @apply tw-h-4 tw-w-4 tw-accent-orange-600;
          }
        }
      }

      .variant-expand-button {
        @apply tw-flex tw-h-11 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-lg tw-text-neutral-500 tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-100 tw-text-neutral-900;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .variant-details {
      @apply tw-border-t tw-border-neutral-200 tw-p-3;

      .variant-details-grid {
        @apply tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row;

        .variant-picture {
          @apply tw-flex tw-w-full tw-flex-none tw-flex-col tw-gap-1 sm:tw-w-40;
        }

        .variant-detail-fields {
          @apply tw-flex tw-w-full tw-flex-col tw-gap-3;

          .field {
            @apply tw-flex tw-flex-col tw-gap-1;

            .field-textarea {
              @apply tw-w-full tw-resize-none tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

              &:focus {
                @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
              }
            }
          }

          .variant-detail-actions {
            @apply tw-flex tw-items-end tw-justify-between tw-gap-3;

            .active-toggle {
              @apply tw-flex tw-flex-col tw-gap-0.5;

              .active-switch {
                @apply tw-m-0 tw-p-0;
              }
            }
          }
        }
      }
    }

    .detail-label {
      @apply tw-text-xs tw-font-medium tw-text-neutral-500;
    }

    .detail-help {
      @apply tw-text-xs tw-text-neutral-500;
    }
  }
</style>
